import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CONSTANTS } from 'src/app/constants';
import { AnalyzersService } from 'src/app/providers/services/analyzers.service';
import { ConfirmationModalService } from 'src/app/providers/services/confirmation-modal.service';
import { MissionsService } from 'src/app/providers/services/missions.service';
import { SkidsService } from 'src/app/providers/services/skids.service';
import { AnalyzerMission } from 'src/app/storage/models/analyzer-mission.model';
import { Gateway } from 'src/app/storage/models/gateway.model';
import { Mission } from 'src/app/storage/models/mission.model';
import { SkidMission } from 'src/app/storage/models/skid-mission.model';

@Component({
    selector: 'app-material-validation',
    templateUrl: './material-validation.component.html',
    styleUrls: ['./material-validation.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MaterialValidationComponent implements OnInit {
  addingPump = false;
  addingGateway = false;
  addingFlowmeter = false;
  addingSiteWellawareId = false;

  missionSkidToUpdate: SkidMission = null;
  missionAnalyzerToUpdate: AnalyzerMission = null;

  siteWellawareId: string | null = null;

  @Input() mission: Mission;
  @Input() missionSkids: SkidMission[];
  @Input() missionAnalyzers: AnalyzerMission[];
  @Output() closeModal = new EventEmitter<any>();

  constructor(
    private confirmationModalService: ConfirmationModalService,
    private translateService: TranslateService,
    private missionsService: MissionsService,
    private skidsService: SkidsService,
    private analyzersService: AnalyzersService
  ) { }

  async ngOnInit(): Promise<void> {
    this.siteWellawareId = this.mission.siteWellawareId ?? null;
    await this.checkMaterialValidity();
  }

  async checkMaterialValidity(): Promise<void> {
    for (let i = 0; i < this.missionSkids.length; i++) {
      const missionSkid = this.missionSkids[i];

      // Check for gateway if mission skid's telemetry supplier is 'ARKEMA'
      if (
        missionSkid.skid.hasFlowmeter &&
        missionSkid.skid.flowmeter.telemetrysuppliername === CONSTANTS.TELEMETRY_SUPPLIER.ARKEMA &&
        missionSkid.skid.gateway == null &&
        missionSkid.gateway == null
      ) {
        await this.addGatewayToMissionSkid(missionSkid);
        return;
      }
    }

    // Check for gateway for each analyzer assigned to a train.
    for (const missionAnalyzer of this.missionAnalyzers ?? []) {
      if (missionAnalyzer.analyzer.telemetrysuppliername === CONSTANTS.TELEMETRY_SUPPLIER.ARKEMA
        && missionAnalyzer.gateway == null) {
        await this.addGatewayToMissionAnalyzer(missionAnalyzer);
        return;
      }
    }

    if ((this.missionAnalyzers ?? []).some(missionAnalyzer =>
        missionAnalyzer.analyzer.telemetrysuppliername === CONSTANTS.TELEMETRY_SUPPLIER.WELLAWARE) &&
        this.siteWellawareId == null
    ) {
      await this.addSiteWellawareId();
      return;
    }

    // Confirm material for valid equipments
    await this.confirmValidation();
  }

  close(): void {
    this.closeModal.emit();
  }

  async addGatewayToMissionSkid(missionSkid: SkidMission): Promise<void> {
    const translations =  await this.translateService.get('MATERIAL').toPromise();
    const title = translations['MISSION_SKID_MISSING_GATEWAY'].replace('{SKID}', missionSkid.skid.skidName);

    // Open gateway selection modal
    this.confirmationModalService.openModal({
      title: `<strong>${title}</strong>`,
      buttons: [
        {
          text: translations['SELECT_GATEWAY'],
          method: async () => {
            this.confirmationModalService.closeModal();
            this.missionSkidToUpdate = missionSkid;
            this.addingGateway = true;
          }
        },
        {
          text: await this.translateService.get('CANCEL').toPromise(),
          method: async () => {
            this.confirmationModalService.closeModal();
            this.close();
          }
        }
      ]
    });
  }

  async addGatewayToMissionAnalyzer(missionAnalyzer: AnalyzerMission): Promise<void> {
    const translations =  await this.translateService.get('MATERIAL').toPromise();
    const title = translations['MISSION_ANALYZER_MISSING_GATEWAY'].replace('{ANALYZER}', missionAnalyzer.analyzerId);

    // Open gateway selection modal
    this.confirmationModalService.openModal({
      title: `<strong>${title}</strong>`,
      buttons: [
        {
          text: translations['SELECT_GATEWAY'],
          method: async () => {
            this.confirmationModalService.closeModal();
            this.missionAnalyzerToUpdate = missionAnalyzer;
            this.addingGateway = true;
          }
        },
        {
          text: await this.translateService.get('CANCEL').toPromise(),
          method: async () => {
            this.confirmationModalService.closeModal();
            this.close();
          }
        }
      ]
    });
  }

  async addSiteWellawareId(): Promise<void> {
    const translations =  await this.translateService.get('MATERIAL').toPromise();
    const title = translations['ENTER_SITEWELLAWAREID'];

    // Open gateway selection modal
    this.confirmationModalService.openModal({
      title: `<strong>${title}</strong>`,
      buttons: [
        {
          text: await this.translateService.get('CONFIRM').toPromise(),
          method: async () => {
            this.confirmationModalService.closeModal();
            this.addingSiteWellawareId = true;
          }
        },
        {
          text: await this.translateService.get('CANCEL').toPromise(),
          method: async () => {
            this.confirmationModalService.closeModal();
            this.close();
          }
        }
      ]
    });
  }

  async confirmValidation(): Promise<void> {
    const translations = await this.translateService.get('MATERIAL').toPromise();

    // Open confirmation modal
    this.confirmationModalService.openModal({
      title: `<strong>${translations['STATUS_COMPLETE']}</strong>`,
      buttons: [
        {
          text: await this.translateService.get('CONFIRM').toPromise(),
          method: async () => {
            this.confirmationModalService.closeModal();
            await this.setMaterialStatus(CONSTANTS.MISSION.CONFIG_STATUS.COMPLETED);
            this.close();
          }
        },
        {
          text: await this.translateService.get('CANCEL').toPromise(),
          method: async () => {
            this.confirmationModalService.closeModal();
            this.close();
          }
        }
      ]
    });
  }

  async setMaterialStatus(status: number): Promise<void> {
    // Update material status
    const isStatusUpdated = await this.missionsService.updateMissionMaterialStatus(this.mission.missionId, status);

    // If update succeeded, set mission material status
    if (isStatusUpdated) {
      this.mission.materialstatus = status;
    }
  }

  async closeAddGatewayModal(gateway?: Gateway): Promise<void> {
    this.addingGateway = false;

    // Set gateway if it is provided
    if (gateway != null) {
      if (this.missionSkidToUpdate != null) {
        await this.updateMissionSkidGateway(this.missionSkidToUpdate, gateway);
      } else if (this.missionAnalyzerToUpdate != null) {
        await this.updateMissionAnalyzerGateway(this.missionAnalyzerToUpdate, gateway);
      }
    }

    // Restart material validation
    this.missionSkidToUpdate = null;
    this.missionAnalyzerToUpdate = null;
    await this.checkMaterialValidity()
  }

  async closeAddSiteWellawareIdModal(siteWellawareId?: string | null): Promise<void> {
    this.addingSiteWellawareId = false;

    // Set siteWellawareId if it is provided
    if (siteWellawareId != null) {
      await this.updateMissionSiteWellawareId(siteWellawareId);
    }

    // Restart material validation
    await this.checkMaterialValidity();
  }

  async updateMissionSiteWellawareId(siteWellawareId): Promise<void> {
    // Update mission siteWellawareId
    const isUpdated = await this.missionsService.updateMissionSiteWellawareId(this.mission.missionId, siteWellawareId);

    // If update suceeded, set siteWellawareId
    if (isUpdated) {
      this.mission.siteWellawareId = siteWellawareId;
      this.siteWellawareId = siteWellawareId;
    }
  }

  async updateMissionSkidGateway(missionSkid: SkidMission, gateway: Gateway): Promise<void> {
    const updatedMissionSkid = new SkidMission(missionSkid);
    updatedMissionSkid.gateway = gateway;
    updatedMissionSkid.gatewayId = gateway.gatewayId;

    // Update mission skid
    const isMissionSkidUpdated = await this.skidsService.addUpdateMissionSkid(
      this.mission.missionId, updatedMissionSkid, missionSkid.injectionpk, missionSkid.trainpk
    );
    
    if (isMissionSkidUpdated) {
      this.missionSkids.filter(assignment => assignment.skidId === missionSkid.skidId).forEach(assignment => {
        assignment.gateway = gateway;
        assignment.gatewayId = gateway.gatewayId;
      });
    }
  }

  async updateMissionAnalyzerGateway(missionAnalyzer: AnalyzerMission, gateway: Gateway): Promise<void> {
    const updatedMissionAnalyzer = new AnalyzerMission(missionAnalyzer);
    updatedMissionAnalyzer.gateway = gateway;
    updatedMissionAnalyzer.gatewayId = gateway.gatewayId;

    // Update mission analyzer
    const isMissionAnalyzerUpdated = await this.analyzersService.addUpdateMissionAnalyzer(this.mission.missionId, updatedMissionAnalyzer);

    if (isMissionAnalyzerUpdated) {
      missionAnalyzer.gateway = gateway;
      missionAnalyzer.gatewayId = gateway.gatewayId;
    }
  }

}
